import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { PlatformApiError, type PlatformApiClient, type CommerceProductImage } from "../api";

function message(error: unknown): string {
  if (error instanceof PlatformApiError && error.code === "version_conflict") {
    return "La imagen cambió en otra sesión. Recargá los datos antes de continuar.";
  }
  return error instanceof Error ? error.message : "No se pudo completar la operación.";
}

/** Convert the API client's relative route into a same-origin browser image URL once. */
export function commercePrivateImagePreviewSrc(
  api: PlatformApiClient, organizationId: string, storeId: string, productId: string, version: number,
): string {
  if (!Number.isSafeInteger(version) || version < 1) throw new Error("Invalid stored image version");
  return "/api/v1/platform" + api.privateCommerceProductImagePath(organizationId, storeId, productId)
    + "?v=" + encodeURIComponent(String(version));
}

/** Private authenticated Commerce image editor; no public storefront or remote URLs. */
export function CommerceProductImagePanel({
  api, organizationId, storeId, productId, productName, editable, onExpired,
}: {
  api: PlatformApiClient;
  organizationId: string;
  storeId: string;
  productId: string;
  productName: string;
  editable: boolean;
  onExpired: () => void;
}) {
  const requestVersion = useRef(0);
  const [meta, setMeta] = useState<CommerceProductImage | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [needsReload, setNeedsReload] = useState(false);

  async function reload() {
    const epoch = ++requestVersion.current;
    setLoading(true);
    setError("");
    setNotice("");
    setConfirmRemove(false);
    try {
      const result = await api.getCommerceProductImage(organizationId, storeId, productId);
      if (requestVersion.current !== epoch) return;
      if (result.image.organizationId !== organizationId ||
        result.image.storeId !== storeId || result.image.productId !== productId) {
        throw new Error("La imagen no pertenece al producto seleccionado.");
      }
      setMeta(result.image);
      setAlt(result.image.altText ?? "");
      setFile(null);
      setNeedsReload(false);
    } catch (cause) {
      if (requestVersion.current !== epoch) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      setError(message(cause));
    } finally {
      if (requestVersion.current === epoch) setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    return () => { ++requestVersion.current; };
    // Panel identity is keyed by the parent to the canonical org/store/product.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, organizationId, storeId, productId]);

  function choose(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.currentTarget.files?.[0] ?? null;
    setFile(chosen);
    setError("");
    setNotice("");
    setConfirmRemove(false);
    if (chosen && (chosen.size > 1_048_576 || chosen.size < 24 ||
      !["image/jpeg", "image/png"].includes(chosen.type))) {
      setFile(null);
      setError("Usá una imagen PNG o JPEG de hasta 1 MiB. Reducí el tamaño antes de cargarla.");
    }
  }

  async function upload() {
    if (!meta || !file || !editable || busy || needsReload || !alt.trim()) return;
    const epoch=requestVersion.current;
    setBusy(true);
    setNotice("");
    setError("");
    try {
      const { image }=await api.uploadCommerceProductImage(
        organizationId,storeId,productId,file,alt,meta.version,
      );
      if (requestVersion.current !== epoch) return;
      if (image.organizationId !== organizationId || image.storeId !== storeId ||
        image.productId !== productId) {
        throw new Error("La respuesta pertenece a otro producto.");
      }
      setMeta(image);
      setFile(null);
      setAlt(image.altText ?? "");
      setNotice("Imagen privada guardada en el catálogo. No está publicada en Internet.");
    } catch (cause) {
      if (requestVersion.current !== epoch) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      if (cause instanceof PlatformApiError && cause.code === "version_conflict") setNeedsReload(true);
      setError(message(cause));
    } finally {
      if (requestVersion.current === epoch) setBusy(false);
    }
  }

  async function remove() {
    if (!meta?.sha256 || !confirmRemove || !editable || busy || needsReload) return;
    const epoch=requestVersion.current;
    setBusy(true);
    setNotice("");
    setError("");
    setConfirmRemove(false);
    try {
      const { image }=await api.removeCommerceProductImage(
        organizationId,storeId,productId,meta.version,
      );
      if (requestVersion.current !== epoch) return;
      if (image.organizationId !== organizationId || image.storeId !== storeId ||
        image.productId !== productId) {
        throw new Error("La respuesta pertenece a otro producto.");
      }
      setMeta(image);
      setFile(null);
      setAlt("");
      setNotice("Imagen retirada. La lectura privada anterior quedó revocada.");
    } catch (cause) {
      if (requestVersion.current !== epoch) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      if (cause instanceof PlatformApiError && cause.code === "version_conflict") setNeedsReload(true);
      setError(message(cause));
    } finally {
      if (requestVersion.current === epoch) setBusy(false);
    }
  }


  return <section className="commerce-image-panel" aria-label={"Imagen de " + productName}>
    <div className="commerce-image-heading">
      <strong>Imagen del producto</strong>
      {meta?.sha256 && <span className="commerce-status" data-status="active">Guardada</span>}
    </div>
    {loading ? <p role="status">Consultando imagen…</p> : <>
      <div className="commerce-image-layout">
        {meta?.sha256
          ? <img src={commercePrivateImagePreviewSrc(api,organizationId,storeId,productId,meta.version)}
              width={180} height={140} alt={meta.altText ?? productName} loading="lazy" />
          : <div className="commerce-image-placeholder">Sin fotografía</div>}
        <div className="commerce-image-editor">
          {editable ? <>
            <label>Archivo PNG/JPEG (máx. 1 MiB)
              <input type="file" accept="image/png,image/jpeg" onChange={choose} disabled={busy || needsReload} />
            </label>
            <label>Descripción de la imagen
              <input value={alt} maxLength={160} onChange={e => setAlt(e.target.value)}
                placeholder={"Ej. " + productName + " visto de frente"} disabled={busy || needsReload} />
            </label>
          </> : <small>Producto archivado: imagen disponible solo para lectura.</small>}
          <div className="commerce-image-actions">
            {editable && <button type="button" className="primary-button"
              disabled={busy || needsReload || !file || !alt.trim()}
              onClick={() => void upload()}>Guardar imagen</button>}
            {editable && meta?.sha256 && <button type="button" disabled={busy || needsReload}
              onClick={() => setConfirmRemove(true)}>Retirar imagen</button>}
            <button type="button" disabled={busy} onClick={() => void reload()}>Recargar</button>
          </div>
        </div>
      </div>
      {confirmRemove && <div className="commerce-image-confirm" role="group" aria-label="Confirmar eliminación de la imagen">
        <p>Se retirará la fotografía privada de este producto.</p>
        <button type="button" onClick={() => setConfirmRemove(false)} disabled={busy}>Cancelar</button>
        <button type="button" onClick={() => void remove()} disabled={busy}>Confirmar retirada</button>
      </div>}
      {notice && <div role="status" className="success-alert">{notice}</div>}
    </>}
    {error && <div role="alert" className="inline-alert">{error} <button type="button" disabled={busy} onClick={() => void reload()}>Reintentar</button></div>}
    <p className="commerce-image-note">Archivo privado, una imagen por producto. Las imágenes públicas se habilitarán con la tienda segura.</p>
  </section>;
}
